import React, { useState, useEffect } from "react";
import {
  X,
  UserPlus,
  Search,
  User,
  Mail,
  Building2,
  CheckCircle,
  AlertCircle,
  Loader,
  Shield,
  ArrowRight,
  Send,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  fetchPotentialAdminsAPI,
  assignChildOrgAdminAPI,
} from "../../actioncreators/actioncreators";

const AssignChildOrgAdmin = ({ isOpen, onClose, organization, onSuccess }) => {
  const [mode, setMode] = useState("select"); // 'select' or 'invite'
  const [potentialAdmins, setPotentialAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeReason, setChangeReason] = useState("");

  // Invite form state
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
  });
  const [inviteErrors, setInviteErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchPotentialAdmins();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = potentialAdmins.filter(
        (user) =>
          user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAdmins(filtered);
    } else {
      setFilteredAdmins(potentialAdmins);
    }
  }, [searchTerm, potentialAdmins]);

  const fetchPotentialAdmins = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPotentialAdminsAPI({
        targetOrgId: organization.org_id,
      });

      if (response?.businessStatusCode === 2) {
        setPotentialAdmins(response.data || []);
        setFilteredAdmins(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching potential admins:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(selectedUser?.user_id === user.user_id ? null : user);
  };

  const validateInviteForm = () => {
    const errors = {};
    if (!inviteData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteData.email)) {
      errors.email = "Invalid email format";
    }
    if (!inviteData.name.trim()) {
      errors.name = "Name is required";
    }
    setInviteErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "select" && !selectedUser) {
      toast.error("Please select a user to assign as admin");
      return;
    }

    if (mode === "invite" && !validateInviteForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        changeReason: changeReason.trim(),
      };

      if (mode === "select") {
        payload.adminUserId = selectedUser.user_id;
      } else {
        payload.inviteEmail = inviteData.email.trim();
        payload.inviteName = inviteData.name.trim();
      }

      const response = await assignChildOrgAdminAPI(organization.org_id, payload);

      if (response?.businessStatusCode === 2) {
        onSuccess(response.data);
      } else {
        toast.error(response?.message || "Failed to assign admin");
      }
    } catch (error) {
      console.error("Error assigning admin:", error);
      toast.error("Failed to assign admin");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      ORG_ADMIN: { bg: "#fef3c7", text: "#d97706" },
      LOCATION_MANAGER: { bg: "#e0e7ff", text: "#4f46e5" },
      MEMBER: { bg: "#f3f4f6", text: "#6b7280" },
    };
    const colors = roleColors[role] || roleColors.MEMBER;
    return (
      <span
        style={{
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: "600",
          backgroundColor: colors.bg,
          color: colors.text,
        }}
      >
        {role?.replace("_", " ")}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "560px",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "slideUp 0.3s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  backgroundColor: "#ff7a00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={24} color="#fff" />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1f2937",
                    margin: 0,
                  }}
                >
                  {organization.admin_name ? "Change" : "Assign"} Organization Admin
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    margin: "2px 0 0 0",
                  }}
                >
                  {organization.org_name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: "8px",
                border: "none",
                backgroundColor: "transparent",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              <X size={20} color="#6b7280" />
            </button>
          </div>

          {/* Current Admin Info */}
          {organization.admin_name && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#fff",
                borderRadius: "8px",
                border: "1px solid #fcd34d",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#d97706",
                  marginBottom: "4px",
                }}
              >
                <AlertCircle size={14} />
                Current admin will be demoted to Employee
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor: "#fef3c7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <User size={16} color="#d97706" />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#1f2937",
                      margin: 0,
                    }}
                  >
                    {organization.admin_name}
                  </p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#6b7280",
                      margin: 0,
                    }}
                  >
                    {organization.admin_email}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mode Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setMode("select")}
            style={{
              flex: 1,
              padding: "14px",
              border: "none",
              backgroundColor: mode === "select" ? "#fff" : "#f9fafb",
              borderBottom: mode === "select" ? "2px solid #ff7a00" : "none",
              fontSize: "13px",
              fontWeight: "600",
              color: mode === "select" ? "#ff7a00" : "#6b7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Users size={16} />
            Select Existing User
          </button>
          <button
            onClick={() => setMode("invite")}
            style={{
              flex: 1,
              padding: "14px",
              border: "none",
              backgroundColor: mode === "invite" ? "#fff" : "#f9fafb",
              borderBottom: mode === "invite" ? "2px solid #ff7a00" : "none",
              fontSize: "13px",
              fontWeight: "600",
              color: mode === "invite" ? "#ff7a00" : "#6b7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Send size={16} />
            Invite New Admin
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: "20px 24px",
            maxHeight: "380px",
            overflowY: "auto",
          }}
        >
          {mode === "select" ? (
            <>
              {/* Search */}
              <div
                style={{
                  position: "relative",
                  marginBottom: "16px",
                }}
              >
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9ca3af",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 12px 12px 40px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
              </div>

              {/* User List */}
              {isLoading ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  <Loader
                    size={24}
                    color="#ff7a00"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  <p style={{ marginTop: "12px" }}>Loading users...</p>
                </div>
              ) : filteredAdmins.length === 0 ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  <Users size={32} color="#d1d5db" />
                  <p style={{ marginTop: "12px" }}>No eligible users found</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {filteredAdmins.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => handleSelectUser(user)}
                      style={{
                        padding: "12px 14px",
                        borderRadius: "10px",
                        border: `2px solid ${
                          selectedUser?.user_id === user.user_id
                            ? "#ff7a00"
                            : "#e5e7eb"
                        }`,
                        backgroundColor:
                          selectedUser?.user_id === user.user_id
                            ? "#fff7ed"
                            : "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "12px" }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor:
                              selectedUser?.user_id === user.user_id
                                ? "#ff7a00"
                                : "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color:
                              selectedUser?.user_id === user.user_id
                                ? "#fff"
                                : "#6b7280",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          {user.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1f2937",
                              margin: 0,
                            }}
                          >
                            {user.full_name}
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              margin: "2px 0 0 0",
                            }}
                          >
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "8px" }}
                      >
                        {getRoleBadge(user.role_name)}
                        {selectedUser?.user_id === user.user_id && (
                          <CheckCircle size={20} color="#ff7a00" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Invite Form */
            <div>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Full Name <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  value={inviteData.name}
                  onChange={(e) =>
                    setInviteData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter admin's full name"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: `1px solid ${
                      inviteErrors.name ? "#dc2626" : "#e5e7eb"
                    }`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                {inviteErrors.name && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#dc2626",
                      marginTop: "4px",
                    }}
                  >
                    {inviteErrors.name}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Email Address <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="admin@example.com"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: `1px solid ${
                      inviteErrors.email ? "#dc2626" : "#e5e7eb"
                    }`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                {inviteErrors.email && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#dc2626",
                      marginTop: "4px",
                    }}
                  >
                    {inviteErrors.email}
                  </p>
                )}
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "8px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "#166534",
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Mail size={14} />
                  An invitation email will be sent to the admin with login instructions.
                </p>
              </div>
            </div>
          )}

          {/* Change Reason */}
          <div style={{ marginTop: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Reason for Change (Optional)
            </label>
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Enter reason for this admin change..."
              rows={2}
              style={{
                width: "100%",
                padding: "12px 14px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f9fafb",
          }}
        >
          <div>
            {mode === "select" && selectedUser && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  color: "#059669",
                }}
              >
                <CheckCircle size={16} />
                {selectedUser.full_name} selected
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "12px 24px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#fff",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                (mode === "select" && !selectedUser) ||
                (mode === "invite" && (!inviteData.email || !inviteData.name))
              }
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                backgroundColor:
                  (mode === "select" && !selectedUser) ||
                  (mode === "invite" && (!inviteData.email || !inviteData.name))
                    ? "#d1d5db"
                    : "#ff7a00",
                fontSize: "14px",
                fontWeight: "600",
                color: "#fff",
                cursor:
                  (mode === "select" && !selectedUser) ||
                  (mode === "invite" && (!inviteData.email || !inviteData.name))
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 2px 8px rgba(255, 122, 0, 0.3)",
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader size={16} className="spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  {mode === "select" ? "Assign Admin" : "Send Invitation"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default AssignChildOrgAdmin;
